import type { Organization } from '@breakoutlearning/firebase-repository/models/Organization'
import { SectionRequest } from '@breakoutlearning/firebase-repository/models/SectionRequest'
import {
  OrganizationInvoiceStatus,
  SectionPaymentMethod,
} from '@breakoutlearning/firebase-repository/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { FullPageSpinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { useBreakoutUser } from 'hooks/profile'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { PendingRequestState } from '../PendingRequestState'
import { RequestedState } from '../RequestedState'
import { BillingPartySection } from './BillingPartySection'
import { EstimatedFields } from './EstimatedField'
import { ProductSelectorSection } from './ProductSelectorSection'
import { type FormValues, getSchema } from './schema'
import { SectionPromotionConfiguration } from './SectionPromotionConfiguration'
import { SettingsWarningBox } from './SettingsWarningBox'
import { safeNonCriticalRandomUUID } from 'util/crypto'

function resolveWhoPaysValue(invoiceStatus: OrganizationInvoiceStatus) {
  if (invoiceStatus === OrganizationInvoiceStatus.alwaysInvoice) {
    return 'invoicing'
  }
  if (invoiceStatus === OrganizationInvoiceStatus.noInvoices) {
    return 'student'
  }

  // undefined means we don't resolve, instructor will choose
  return undefined
}

export const BillingConfiguration = observer(function BillingConfiguration({
  invoiceStatus,
  sectionRequest,
  setWantsInvoicing,
  sectionOrganization,
}: {
  invoiceStatus: OrganizationInvoiceStatus
  sectionRequest: SectionRequest | null
  setWantsInvoicing: (value: boolean) => void
  sectionOrganization: Organization
}) {
  const [requestSubmitted, setRequestSubmitted] = useState(false)
  const [selectedUserPromotionIds, setSelectedUserPromotionIds] = useState<
    string[]
  >([])
  const user = useBreakoutUser()
  const userPromotions = user.userPromotions.models

  const sectionCubit = useInstructorSectionCubit()
  const repository = useRepository()
  const userCount = useMemo(() => {
    return sectionCubit.section.data.userIds.length
  }, [sectionCubit.section])

  const draftSectionRequest = useMemo(() => {
    const request = SectionRequest.empty(repository)
    request.replaceData({
      ...request.data,
      sectionRequestAssignmentCount: 4,
      sectionRequestStudentCount: 200,
    })
    return request
  }, [repository])
  const { t, tt } = useTranslationTyped()

  const [requireProductSelection, setRequireProductSelection] = useState(false)

  const schema = useMemo(
    () => getSchema(t, requireProductSelection),
    [t, requireProductSelection]
  )

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      estimatedNumberOfExperiences: 4,
      estimatedNumberOfUsers: userCount || 200,
      billingParty: resolveWhoPaysValue(invoiceStatus),
    },
  })
  const { control, handleSubmit, formState } = form

  const estimatedNumberOfUsers = form.watch('estimatedNumberOfUsers')
  const estimatedNumberOfExperiences = form.watch(
    'estimatedNumberOfExperiences'
  )
  const billingParty = form.watch('billingParty')

  const onChange = useCallback(() => {
    draftSectionRequest.replaceData({
      ...draftSectionRequest.data,
      sectionRequestAssignmentCount: estimatedNumberOfExperiences,
      sectionRequestStudentCount: estimatedNumberOfUsers,
    })
  }, [
    draftSectionRequest,
    estimatedNumberOfExperiences,
    estimatedNumberOfUsers,
  ])

  const needsInvoiceRequest =
    billingParty === 'invoicing' &&
    invoiceStatus === OrganizationInvoiceStatus.adminApproved

  const onSubmit = useCallback(
    async (data: FormValues) => {
      const section = sectionCubit.section
      const organizationId = section.data.organizationId
      const billingSubmissionId = safeNonCriticalRandomUUID()

      repository.logEvent('lti_section_billing_started', {
        userId: repository.uid,
        sectionId: section.id,
        submissionId: billingSubmissionId,
      })
      if (!organizationId) {
        repository.logEvent('lti_section_billing_error', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          message: 'Organization ID not found for section',
        })
        alert('Organization ID not found')
        return
      }

      // create snapshot of selected product state before async calls
      // we don't want the form manipulations to affect the data mid execute
      const productToSave =
        data.selectedProduct && data.selectedProduct.productId
          ? ({
              productId: data.selectedProduct.productId,
              isCustom: data.selectedProduct.isCustom,
            } as const)
          : null
      const estimatedNumberOfUsers = data.estimatedNumberOfUsers
      const estimatedNumberOfExperiences = data.estimatedNumberOfExperiences

      // Step 1: Create Invoice Request if needed
      // It must happen as the first thing, as the existence of the request
      // defines which screen is visible
      if (needsInvoiceRequest) {
        const sectionRequest = {
          sectionRequestAssignmentCount: estimatedNumberOfExperiences || 0,
          sectionRequestStudentCount: estimatedNumberOfUsers || 0,
          organizationId: organizationId,
          requestedAt: new Date(),
          sectionId: sectionCubit.sectionId,
        }
        await sectionCubit.createSectionInvoiceRequest({
          sectionId: sectionCubit.sectionId,
          sectionRequest,
        })
        const sectionRequestForLogging = {
          ...sectionRequest,
          requestedAt: sectionRequest.requestedAt.toISOString(),
        }
        repository.logEvent('lti_section_billing_request_created', {
          userId: repository.uid,
          submissionId: billingSubmissionId,
          ...sectionRequestForLogging,
        })
        setRequestSubmitted(true)
      } else {
        repository.logEvent('lti_section_billing_request_not_created', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
        })
      }

      // Step 2: Select Product
      if (productToSave) {
        repository.logEvent('lti_section_billing_product_selected', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          expectedStudents: data.estimatedNumberOfUsers,
          productId: productToSave.productId,
          isCustom: productToSave.isCustom,
        })
        // this code should run when we want to select the product
        await sectionCubit.updateSection({
          expectedStudents: estimatedNumberOfUsers,
          selectedProduct: productToSave,
        })
        repository.logEvent('lti_section_billing_product_created', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          expectedStudents: data.estimatedNumberOfUsers,
          productId: productToSave.productId,
          isCustom: productToSave.isCustom,
        })
      } else {
        repository.logEvent('lti_section_billing_product_not_selected', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          expectedStudents: data.estimatedNumberOfUsers,
          sectionPaymentMethod: SectionPaymentMethod.token,
        })
        await sectionCubit.updateSection({
          sectionPaymentMethod: SectionPaymentMethod.token,
          expectedStudents: estimatedNumberOfUsers,
        })
        repository.logEvent('lti_section_billing_section_updated', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          expectedStudents: data.estimatedNumberOfUsers,
          sectionPaymentMethod: SectionPaymentMethod.token,
        })
      }

      // Step 3: Redeem promotions
      const promotions = userPromotions?.filter((p) =>
        selectedUserPromotionIds.includes(p.id)
      )

      if (promotions && promotions.length > 0) {
        repository.logEvent('lti_section_billing_promotions_redeemed', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          promotionIds: promotions.map((p) => p.id).join(','),
        })
        await sectionCubit.redeemPromotions(section.id, promotions)
        repository.logEvent('lti_section_billing_promotions_redeemed_success', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
          promotionIds: promotions.map((p) => p.id).join(','),
        })
      } else {
        repository.logEvent('lti_section_billing_no_promotions_redeemed', {
          userId: repository.uid,
          sectionId: section.id,
          submissionId: billingSubmissionId,
        })
      }

      repository.logEvent('lti_section_billing_completed', {
        userId: repository.uid,
        sectionId: section.id,
        submissionId: billingSubmissionId,
      })

      setWantsInvoicing(false)
    },
    [
      needsInvoiceRequest,
      repository,
      sectionCubit,
      selectedUserPromotionIds,
      setWantsInvoicing,
      userPromotions,
    ]
  )

  const createNewRequest = useCallback(async () => {
    setRequestSubmitted(false)
    setWantsInvoicing(true)

    if (sectionRequest) {
      await sectionCubit.withdrawSectionRequest(sectionRequest.id)
    }
  }, [sectionCubit, sectionRequest, setWantsInvoicing])

  const setToStudentPay = useCallback(async () => {
    setRequestSubmitted(false)
    setWantsInvoicing(false)

    if (sectionRequest) {
      await sectionCubit.withdrawSectionRequest(sectionRequest.id)
      await sectionCubit.setToStudentPay()
    }
  }, [sectionCubit, sectionRequest, setWantsInvoicing])

  const requestIsPending =
    !!sectionRequest?.isPending || !!sectionRequest?.isRejected

  if (sectionCubit.section.isLoading) {
    return <FullPageSpinner />
  }

  if (requestIsPending) {
    return (
      <PendingRequestState
        sectionRequest={sectionRequest!}
        setToStudentPay={setToStudentPay}
        createNewRequest={createNewRequest}
      />
    )
  }

  if (requestSubmitted) {
    return <RequestedState setWantsInvoicing={setWantsInvoicing} />
  }

  return (
    <div className="invoicing flex min-h-full w-full flex-col items-start justify-center">
      <div className="ml-[5vw] max-w-[400px] py-4">
        <div className="mb-5">
          <h1 className="text-headline-large">
            {tt.lti.configuration.billing_settings()}
          </h1>
          <div className="text-body-large">
            {tt.lti.configuration.billing_settings_description()}
          </div>
        </div>

        <SettingsWarningBox />

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <BillingPartySection invoiceStatus={invoiceStatus} form={form} />

          <ProductSelectorSection
            form={form}
            sectionOrganization={sectionOrganization}
            onRequireProductSelectionChange={(value) => {
              setRequireProductSelection(value)
            }}
          />

          <EstimatedFields
            control={control}
            onChange={onChange}
            requireProductSelection={requireProductSelection}
          />

          {needsInvoiceRequest && (
            <div className="pl-4 text-body-medium">
              {tt.lti.configuration.needs_invoicing_request_info()}
            </div>
          )}

          <SectionPromotionConfiguration
            onSelectedUserPromotionIdsChange={setSelectedUserPromotionIds}
          />

          <div className="flex justify-end">
            <BreakoutButton
              kind="accent"
              type="submit"
              size="medium"
              className="bg-fixed-accent-color"
              disabled={formState.isSubmitting}
            >
              {tt.lti.configuration.billing_save()}
            </BreakoutButton>
          </div>
        </form>
      </div>
    </div>
  )
})
